import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import QuestionsTabs from "./QuestionsTabs";
import {
  getBatchSubjectList,
} from "utils/metadata-split/metaDataApiFetch";

type Props = {
  index: number;
  data: any;
  onChange: (newData: any) => void;
  onRemove: () => void;
  examPaper?: any;
};

function SectionForm({ index, data, onChange, onRemove, examPaper }: Props) {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const handleFieldChange = (field: string, value: any) => {
    const newData = { ...data, [field]: value };

    // If subject changes, update all questions in this section
    if (field === 'subject' || field === 'subjectName') {
      const updatedQuestions = (data.questions || []).map((q: any) => ({
        ...q,
        subject: field === 'subject' ? value : q.subject,
        subjectName: field === 'subjectName' ? value : q.subjectName,
        // Reset chapter and topic when subject changes
        ...(field === 'subject' && {
          chapter: '',
          chapterName: '',
          topic: '',
          topicName: ''
        })
      }));
      newData.questions = updatedQuestions;
    }

    onChange(newData);
  };

  const handleQuestionsChange = (newQuestions: any[]) => {
    onChange({ ...data, questions: newQuestions });
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const res: any = await getBatchSubjectList();
        if (res && Array.isArray(res[0])) {
          setSubjects(res[0]);
        }
      } catch (error) {
        console.error("Error fetching subjects", error);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, []);

  return (
    <Card
      elevation={2}
      sx={{
        position: "relative",
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with Delete Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Section {index + 1}
          </Typography>
          <IconButton
            onClick={onRemove}
            color="error"
            sx={{
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              '&:hover': {
                backgroundColor: 'error.main'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {/* Section Configuration */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Section Title"
              size="small"
              fullWidth
              value={data.title || ""}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              placeholder="e.g., Mathematics - Algebra"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select
                value={String(data.subject ?? "")}
                onChange={(e) => {
                  const selectedSubject = subjects.find(
                    (s) => s.subject_id === e.target.value
                  );
                  // Update both subject and subjectName in a single call
                  const newData = {
                    ...data,
                    subject: String(e.target.value),
                    subjectName: selectedSubject?.subject_name || ""
                  };

                  // Update all questions in this section with new subject
                  const updatedQuestions = (data.questions || []).map((q: any) => ({
                    ...q,
                    subject: String(e.target.value),
                    subjectName: selectedSubject?.subject_name || "",
                    chapter: '',
                    chapterName: '',
                    topic: '',
                    topicName: ''
                  }));
                  newData.questions = updatedQuestions;

                  onChange(newData);
                }}
                label="Subject"
                sx={{
                  borderRadius: 2
                }}
              >
                <MenuItem value="">Select Subject</MenuItem>
                {loadingSubjects ? (
                  <MenuItem disabled>
                    <CircularProgress size={18} /> Loading...
                  </MenuItem>
                ) : (
                  subjects.map((sub: any) => (
                    <MenuItem key={sub.subject_id} value={sub.subject_id}>
                      {sub.subject_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Section Marks"
              type="number"
              size="small"
              fullWidth
              value={data.marks}
              onChange={(e) => handleFieldChange("marks", Number(e.target.value))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Choice Count"
              type="number"
              size="small"
              fullWidth
              value={data.choiceCount || 1}
              onChange={(e) => handleFieldChange("choiceCount", Number(e.target.value))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Questions Section */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Questions
          </Typography>
          <QuestionsTabs
            questions={data.questions || []}
            onChange={handleQuestionsChange}
            examPaper={examPaper}
            sectionSubject={data.subject}
            sectionSubjectName={data.subjectName}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default SectionForm;